import { notFound } from "@tanstack/react-router";
import type { WikiPagePublic } from "@softmax/observatory-client/types";
import { buildHref, matchRoute } from "@softmax/web-addresses";
import { curatedText } from "@softmax/web-features/coworlds";

import { readCommunity } from "../community.api";
import { type CommunityDestination, communityRoom } from "../community-room";

export async function loadWikiPage({ href, signal }: CommunityDestination) {
  const route = matchRoute(new URL(href, window.location.origin).pathname);

  if (route.name !== "wikiPage" && route.name !== "game.wikiPage")
    throw notFound();

  const room = await communityRoom({ href, signal });
  const initialDataUpdatedAt = Date.now();
  const result = await readCommunity<WikiPagePublic>({
    signal,
    path: `/v2/wikis/${encodeURIComponent(room.forumSlug)}/pages/${route.page.map(encodeURIComponent).join("/")}`,
  });
  const pageTitle =
    result.status === "ok"
      ? `${room.game ? curatedText(result.data.title) : result.data.title} · `
      : "";
  return {
    room,
    pageSlug: route.page.join("/"),
    page: result.status === "ok" ? result.data : null,
    missing: result.status === "not-found",
    initialDataUpdatedAt,
    metadata: {
      title: `${pageTitle}${room.game ? `${room.displayName} ` : ""}Wiki · Softmax`,
      description: `Read the ${room.displayName} community wiki.`,
      canonical: room.game
        ? buildHref({
            name: "game.wikiPage",
            game: room.game,
            page: route.page,
          })
        : buildHref({ name: "wikiPage", page: route.page }),
      type: "article",
      index: room.indexable && result.status === "ok",
      follow: room.follow,
    },
  };
}

export async function wikiRedirect({ href, signal }: CommunityDestination) {
  const room = await communityRoom({ href, signal });
  const url = new URL(href, window.location.origin);
  const search = url.searchParams;
  return room.game
    ? buildHref({
        name: "game.wikiPage",
        game: room.game,
        page: ["main"],
        search,
        hash: url.hash,
      })
    : buildHref({ name: "wikiPage", page: ["main"], search, hash: url.hash });
}
